import { Fragment, useEffect, useState } from 'react';
import { RadioGroup as HUIRadioGroup } from '@headlessui/react';
import { trimClassname } from 'src/utils/styleHelper';

import {
    markFieldFocus,
    trackSelection,
    uniqueIdTransfer,
    useFieldValidation,
} from '@/apiCalls/uniqueIDTransferHelper';
import { selectIsLoading } from '@/context/display';
import { useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { defaultRequiredMessage } from '@/utils/fieldMessages';

import { EEventType } from '../eventType';
import { FieldLabel } from '../FieldLabel';

export const testId = 'RadioGroup';
interface IOption {
    label: string;
    value: string | boolean | undefined;
    id: string;
}

export interface IRadioGroup {
    title: string | null;
    options: IOption[];
    showErrors: boolean;
    /** Message shown under the group. Defaults to a required prompt. */
    message?: string;
    onChange: (value: string | boolean | undefined) => void;
    eventType?: EEventType;
    value: string | boolean | number | undefined | null;
    tooltipContent?: string;
    /**
     * Always-visible guidance under the label. Use it where a choice is easy to
     * get wrong — unlike `tooltipContent`, someone who is confidently mistaken
     * still reads it. Not an error: `message` remains the error slot.
     */
    helperText?: string;
    /** Field wrappers are required by default; optional fields opt out. */
    required?: boolean;
    className?: string;
}

export function RadioGroup({
    title,
    options,
    value,
    showErrors,
    message,
    onChange,
    eventType = EEventType.CLICK,
    tooltipContent,
    helperText,
    required = true,
    className,
}: IRadioGroup): JSX.Element {
    // A radio group has no id of its own; an error applies to the whole group,
    // so the first option's id stands for it.
    useFieldValidation(options[0]?.id, showErrors);

    const groupMessageId = `${options[0]?.id}-message`;
    const { segmentedOptions } = useCopy();
    // Same condition as haveErrors, so the message and the styling agree.
    const resolvedMessage =
        message ??
        (showErrors && !value
            ? defaultRequiredMessage(
                  typeof title === 'string' ? title : undefined
              )
            : undefined);

    // const componentOptions: string[] = [];
    const [optionsRows, setOptionsRows] = useState<IOption[][]>([]);
    const [flexWrap, setFlexWrap] = useState<boolean>(false);
    const [useIncreasedHeight, setUseIncreasedHeight] =
        useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<
        string | boolean | number | undefined
    >(value || undefined);
    const isLoading = useAppSelector(selectIsLoading);

    useEffect(() => {
        // Chose the list style based on the number of options
        const optionsLength = options.length;
        let size: [number, number] | null = null; // [columns, rows]

        if (optionsLength <= 3) {
            size = [optionsLength, 1];
        } else if (optionsLength === 4) {
            size = [2, 2];
            setUseIncreasedHeight(true);
        } else if (optionsLength === 6) {
            size = [3, 2];
            setUseIncreasedHeight(true);
        } else if (optionsLength === 8) {
            size = [2, 4];
            setUseIncreasedHeight(true);
        } else if (optionsLength === 9) {
            size = [3, 3];
            setUseIncreasedHeight(true);
        } else {
            setFlexWrap(true);
        }

        // Separate options into rows and columns
        const rows: IOption[][] = [];

        if (size) {
            for (let ri = 0; ri < size[1]; ri++) {
                const row: IOption[] = [];
                for (let ci = ri * size[0]; ci < ri * size[0] + size[0]; ci++) {
                    row.push(options[ci]);
                }
                rows.push(row);
            }
        } else {
            rows[0] = options; // First row to have all columns
        }

        setOptionsRows(rows);
    }, []);

    function haveErrors(): boolean {
        return showErrors && !selectedValue;
    }

    function handleChange(clickedOption: IOption): void {
        setSelectedValue(clickedOption.value);

        // Callback
        onChange(clickedOption.value);

        // Tracking
        uniqueIdTransfer({
            eventType: eventType,
            applicationData: {
                id: clickedOption.id,
            },
        });
        trackSelection(clickedOption.id);
    }

    return (
        <div
            data-testid={testId}
            className={segmentedOptions ? 'py-[7px]' : 'py-2'}
        >
            <HUIRadioGroup
                value={selectedValue}
                onChange={handleChange as any}
                onFocus={(): void => markFieldFocus(options[0]?.id)}
            >
                {/* Title */}
                {title && (
                    <FieldLabel
                        title={title}
                        tooltipContent={tooltipContent}
                        required={required}
                        as='p'
                        className='fieldHeading '
                    >
                        <HUIRadioGroup.Label className='sr-only'>
                            {title}
                        </HUIRadioGroup.Label>
                    </FieldLabel>
                )}
                {helperText && (
                    <p
                        data-testid='radioGroupHelperText'
                        className='px-3 pb-2 text-xs leading-snug text-secondary'
                    >
                        {helperText}
                    </p>
                )}
                {optionsRows.map((columns, ri) => {
                    const isFirstRow = ri === 0;
                    const isLastRow = ri === optionsRows.length - 1;

                    return (
                        <div
                            data-testid='radioGroupOptionDiv'
                            // A tinted track holding the options, so the
                            // selected one reads as a card lifted out of it
                            // rather than a black block stamped over it.
                            className={`_flex justify-center items-center w-full ${
                                flexWrap
                                    ? 'flex-wrap space-y-2'
                                    : segmentedOptions
                                    ? 'bg-optionTrack rounded-[10px] p-[3px] gap-1'
                                    : ''
                            }`}
                            key={ri}
                        >
                            {columns.map((option, ci) => {
                                const isFirstColumn = ci === 0;
                                const isLastColumn = ci === columns.length - 1;

                                return (
                                    // Outside Card
                                    <Fragment key={ri * columns.length + ci}>
                                        <HUIRadioGroup.Option
                                            data-googleid={option.id}
                                            value={option}
                                            data-testid='radioGroupOptionCard'
                                            disabled={isLoading ? true : false}
                                            className={`
                                                ${
                                                    !flexWrap &&
                                                    isFirstRow &&
                                                    isFirstColumn
                                                        ? 'rounded-tl-[10px] '
                                                        : ''
                                                }
                                                ${
                                                    !flexWrap &&
                                                    isFirstRow &&
                                                    isLastColumn
                                                        ? 'rounded-tr-[10px] '
                                                        : ''
                                                }
                                                ${
                                                    !flexWrap &&
                                                    isLastRow &&
                                                    isFirstColumn
                                                        ? 'rounded-bl-[10px] '
                                                        : ''
                                                }
                                                ${
                                                    !flexWrap &&
                                                    isLastRow &&
                                                    isLastColumn
                                                        ? 'rounded-br-[10px] '
                                                        : ''
                                                }
                                                ${
                                                    flexWrap
                                                        ? 'rounded-[10px]'
                                                        : ''
                                                }
                                                ${
                                                    haveErrors()
                                                        ? errorClass
                                                        : ''
                                                }
                                                relative _flex
                                                ${
                                                    !isLoading
                                                        ? `cursor-pointer ${
                                                              segmentedOptions
                                                                  ? ''
                                                                  : 'hover:border-secondary'
                                                          }`
                                                        : ''
                                                }
                                                hover:z-10
                                                hover:box-border
                                                w-full
                                                transition
                                                ease-in-out
                                                ${
                                                    // 40px inside a track that
                                                    // adds 4px either side, so
                                                    // the control is 48px tall
                                                    // overall. At h-11 a pair
                                                    // of these read heavier
                                                    // than the questions above
                                                    // them.
                                                    useIncreasedHeight
                                                        ? 'h-12'
                                                        : segmentedOptions
                                                        ? 'h-[38px]'
                                                        : 'h-11'
                                                }
                                                ${
                                                    flexWrap || !segmentedOptions
                                                        ? 'border -mx-[0.3px]'
                                                        : 'rounded-[8px]'
                                                }
                                                ${
                                                    selectedValue ===
                                                    option.value
                                                        ? segmentedOptions
                                                            ? 'bg-white shadow-md text-secondary'
                                                            : '!bg-secondary text-white'
                                                        : // A bordered option
                                                          // paints its own
                                                          // surface, so it
                                                          // still reads as a
                                                          // button on a tinted
                                                          // panel. A segmented
                                                          // one stays clear on
                                                          // purpose: the track
                                                          // behind it is what
                                                          // makes the selected
                                                          // pill look lifted.
                                                        flexWrap ||
                                                          !segmentedOptions
                                                        ? 'bg-white text-textBody'
                                                        : 'text-textBody'
                                                }
                                            `}
                                        >
                                            {({ checked }): JSX.Element => (
                                                <HUIRadioGroup.Label
                                                    as='p'
                                                    data-testid='radioGroupOptionText'
                                                    className={trimClassname(
                                                        // leading-tight, not
                                                        // text-sm's own 20px:
                                                        // a label that wraps
                                                        // sits too far apart
                                                        // at that. A single
                                                        // line is centred in a
                                                        // fixed-height option,
                                                        // so nothing else
                                                        // moves.
                                                        `centerFull px-3 text-center font-bold text-sm leading-tight ${
                                                            selectedValue ===
                                                            option.value
                                                                ? segmentedOptions
                                                                    ? 'text-secondary'
                                                                    : 'text-white'
                                                                : 'text-textBody'
                                                        } ${className}`
                                                    )}
                                                >
                                                    {option.label}
                                                </HUIRadioGroup.Label>
                                            )}
                                        </HUIRadioGroup.Option>
                                    </Fragment>
                                );
                            })}
                        </div>
                    );
                })}
            </HUIRadioGroup>
            <div
                className={`messageSlot ${
                    resolvedMessage ? 'messageSlotOpen' : ''
                }`}
            >
                <div>
                    <p
                        id={groupMessageId}
                        data-testid='radioGroupMessage'
                        role='alert'
                        className='px-3 pt-1 text-xs leading-snug text-errorText font-semibold'
                    >
                        {resolvedMessage}
                    </p>
                </div>
            </div>
        </div>
    );
}

// For testing purposes, constants and exported instead of being inlined
export const checkedClass = 'text-white';
export const uncheckedClass = 'text-gray-900';
export const errorClass = 'error';
